import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  Chip,
  Collapse,
  InputAdornment,
  Link,
  ListSubheader,
  MenuItem,
  TextField,
} from '@mui/material';
import { Search as SearchIcon, TuneOutlined as TuneIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { TransactionFilters } from '../../services/transactionApi';
import { useTransactions } from '../../contexts/TransactionContext';
import { useDashboardCategories } from '../../hooks/useDashboardCategories';
import { getDashboardCategoriesForType } from '../../utils/categoryMapping';
import { tokens } from '../../theme/tokens';
import { detectPeriod, Period, PERIOD_CHIPS, periodRange } from './periods';

interface TransactionFiltersProps {
  onFiltersChange: (filters: TransactionFilters) => void;
  /** changing it puts the filters back to "this month" (the list's "clear filters") */
  resetSignal?: number;
}

const SEARCH_DELAY_MS = 350;
const TYPES = ['', 'expense', 'income', 'transfer'] as const;

/** Filters as the URL carries them, so a filtered list can be shared or reloaded. */
const fromParams = (params: URLSearchParams): TransactionFilters => {
  const filters: TransactionFilters = {
    page: parseInt(params.get('page') || '1', 10),
    limit: parseInt(params.get('limit') || '20', 10),
    sortBy: 'date',
    sortOrder: 'DESC',
  };
  const text = ['startDate', 'endDate', 'type', 'status', 'category', 'dashboardCategory', 'subcategory', 'currency', 'merchantName', 'search'] as const;
  text.forEach((key) => {
    const value = params.get(key);
    if (value) (filters as Record<string, unknown>)[key] = value;
  });
  if (params.get('minAmount')) filters.minAmount = parseFloat(params.get('minAmount')!);
  if (params.get('maxAmount')) filters.maxAmount = parseFloat(params.get('maxAmount')!);
  return filters;
};

const toParams = (filters: TransactionFilters): URLSearchParams => {
  const params = new URLSearchParams();
  params.set('page', String(filters.page ?? 1));
  params.set('limit', String(filters.limit ?? 20));
  Object.entries(filters).forEach(([key, value]) => {
    if (['page', 'limit', 'sortBy', 'sortOrder', 'contextId'].includes(key)) return;
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  });
  // "all time" has no dates; say so, or a reload falls back to this month
  if (!filters.startDate && !filters.endDate) params.set('period', 'all');
  return params;
};

/**
 * Lançamentos' filters: the period as chips, a search box, and the rest
 * (type, status, category, place, amount) one tap away, each shown as a
 * removable chip once set. The list is always newest first, grouped by day.
 */
export const TransactionFiltersComponent: React.FC<TransactionFiltersProps> = ({ onFiltersChange, resetSignal }) => {
  const { t } = useTranslation('transactions');
  const { state, loadMerchants } = useTransactions();
  const { getDashboardCategoryName } = useDashboardCategories();
  const [searchParams, setSearchParams] = useSearchParams();
  const [expanded, setExpanded] = useState(false);
  const [period, setPeriod] = useState<Period>('thisMonth');
  const [filters, setFilters] = useState<TransactionFilters>({ page: 1, limit: 20, sortBy: 'date', sortOrder: 'DESC' });
  const [searchText, setSearchText] = useState('');
  const initialized = useRef(false);
  const onChangeRef = useRef(onFiltersChange);
  onChangeRef.current = onFiltersChange;

  useEffect(() => {
    loadMerchants();
  }, [loadMerchants]);

  const apply = useCallback(
    (next: TransactionFilters) => {
      setFilters(next);
      setSearchParams(toParams(next), { replace: true });
      onChangeRef.current(next);
    },
    [setSearchParams],
  );

  // From the URL once, with "this month" when it names no dates.
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    const initial = fromParams(searchParams);
    if (!initial.startDate && !initial.endDate && searchParams.get('period') !== 'all') {
      Object.assign(initial, periodRange('thisMonth'));
    }
    setPeriod(detectPeriod(initial.startDate, initial.endDate));
    setSearchText(initial.search ?? '');
    apply(initial);
  }, [searchParams, apply]);

  // The search applies once typing pauses, not on every key.
  useEffect(() => {
    if (!initialized.current || (filters.search ?? '') === searchText) return undefined;
    const timer = setTimeout(() => apply({ ...filters, search: searchText || undefined, page: 1 }), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [searchText, filters, apply]);

  const set = (key: keyof TransactionFilters, value: unknown) =>
    apply({ ...filters, [key]: value === '' || value === null ? undefined : value, page: 1 });

  const choosePeriod = (next: Period) => {
    setPeriod(next);
    if (next === 'custom') {
      setExpanded(true);
      return;
    }
    const { startDate, endDate } = periodRange(next);
    apply({ ...filters, startDate, endDate, page: 1 });
  };

  const changeDate = (key: 'startDate' | 'endDate', value: string) => {
    const next = { ...filters, [key]: value || undefined, page: 1 };
    setPeriod(detectPeriod(next.startDate, next.endDate) === 'allTime' ? 'custom' : detectPeriod(next.startDate, next.endDate));
    apply(next);
  };

  const categoryGroups = useMemo(
    () =>
      (['expense', 'income'] as const).map((type) => ({
        type,
        options: getDashboardCategoriesForType(type).map((key) => ({ key, name: getDashboardCategoryName(type, key) })),
      })),
    [getDashboardCategoryName],
  );
  const categoryName = (key: string) =>
    categoryGroups.flatMap((group) => group.options).find((option) => option.key === key)?.name ?? key;

  // What is set beyond the period, each as a chip that clears it.
  const active: Array<{ key: keyof TransactionFilters; label: string }> = [];
  if (filters.type) active.push({ key: 'type', label: t(`filters.type.${filters.type}`) });
  if (filters.status) active.push({ key: 'status', label: t(`filters.status.${filters.status}`) });
  if (filters.dashboardCategory) active.push({ key: 'dashboardCategory', label: categoryName(filters.dashboardCategory) });
  if (filters.merchantName) active.push({ key: 'merchantName', label: filters.merchantName });
  if (filters.minAmount !== undefined) active.push({ key: 'minAmount', label: t('filters.amount.fromChip', { value: filters.minAmount }) });
  if (filters.maxAmount !== undefined) active.push({ key: 'maxAmount', label: t('filters.amount.toChip', { value: filters.maxAmount }) });

  const clearAll = () => {
    setSearchText('');
    setPeriod('thisMonth');
    apply({ page: 1, limit: filters.limit ?? 20, sortBy: 'date', sortOrder: 'DESC', ...periodRange('thisMonth') });
  };
  const dirty = active.length > 0 || Boolean(filters.search) || period !== 'thisMonth';

  const clearRef = useRef(clearAll);
  clearRef.current = clearAll;
  useEffect(() => {
    if (resetSignal) clearRef.current();
  }, [resetSignal]);

  return (
    <Box
      component='section'
      aria-label={t('filters.title')}
      sx={{ bgcolor: tokens.surface, border: `1px solid ${tokens.line}`, borderRadius: `${tokens.radius.card}px`, p: { xs: 1.75, md: 2.25 }, display: 'flex', flexDirection: 'column', gap: 1.5 }}
    >
      <Box
        role='group'
        aria-label={t('filters.timePeriod.label')}
        sx={{ display: 'flex', gap: 0.75, overflowX: 'auto', mx: { xs: -1.75, md: 0 }, px: { xs: 1.75, md: 0 }, pb: 0.25, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
      >
        {PERIOD_CHIPS.map((chip) => {
          const selected = period === chip;
          return (
            <ButtonBase
              key={chip}
              aria-pressed={selected}
              onClick={() => choosePeriod(chip)}
              sx={{
                flexShrink: 0,
                minHeight: 38,
                px: 1.75,
                borderRadius: 99,
                font: 'inherit',
                fontSize: 14,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                border: `1px solid ${selected ? tokens.accent : tokens.line}`,
                bgcolor: selected ? tokens.accent : tokens.surface,
                color: selected ? '#FFFFFF' : tokens.ink2,
                '&:hover': { borderColor: selected ? tokens.accent : tokens.ink3 },
                '&.Mui-focusVisible': { outline: `2px solid ${tokens.accent}`, outlineOffset: 2 },
              }}
            >
              {t(`filters.timePeriod.${chip}`)}
            </ButtonBase>
          );
        })}
      </Box>

      {period === 'custom' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.25, maxWidth: 420 }}>
          <TextField type='date' size='small' label={t('filters.dates.start')} value={filters.startDate ?? ''} onChange={(event) => changeDate('startDate', event.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField type='date' size='small' label={t('filters.dates.end')} value={filters.endDate ?? ''} onChange={(event) => changeDate('endDate', event.target.value)} InputLabelProps={{ shrink: true }} />
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          size='small'
          fullWidth
          type='search'
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder={t('filters.search.placeholder')}
          inputProps={{ 'aria-label': t('filters.search.label') }}
          InputProps={{ startAdornment: <InputAdornment position='start'><SearchIcon sx={{ color: tokens.ink3 }} /></InputAdornment> }}
        />
        <Button
          variant={expanded ? 'contained' : 'outlined'}
          startIcon={<TuneIcon />}
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          sx={{ flexShrink: 0, minHeight: 40 }}
        >
          {t('filters.more')}
          {active.length > 0 && ` · ${active.length}`}
        </Button>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1.5, pt: 0.5 }}>
          <TextField select size='small' label={t('filters.type.label')} value={filters.type ?? ''} onChange={(event) => set('type', event.target.value)}>
            {TYPES.map((type) => (
              <MenuItem key={type || 'all'} value={type}>{type ? t(`filters.type.${type}`) : t('filters.type.all')}</MenuItem>
            ))}
          </TextField>
          <TextField select size='small' label={t('filters.status.label')} value={filters.status ?? ''} onChange={(event) => set('status', event.target.value)}>
            <MenuItem value=''>{t('filters.status.all')}</MenuItem>
            <MenuItem value='confirmed'>{t('filters.status.confirmed')}</MenuItem>
            <MenuItem value='pending'>{t('filters.status.pending')}</MenuItem>
            <MenuItem value='cancelled'>{t('filters.status.cancelled')}</MenuItem>
          </TextField>
          <TextField
            select
            size='small'
            label={t('filters.category.label')}
            value={filters.dashboardCategory ?? ''}
            onChange={(event) => set('dashboardCategory', event.target.value)}
          >
            <MenuItem value=''>{t('filters.category.all')}</MenuItem>
            {categoryGroups.flatMap((group) => [
              <ListSubheader key={`h-${group.type}`}>{t(`filters.type.${group.type}`)}</ListSubheader>,
              ...group.options.map((option) => (
                <MenuItem key={`${group.type}-${option.key}`} value={option.key}>{option.name}</MenuItem>
              )),
            ])}
          </TextField>
          <Autocomplete
            size='small'
            options={state.merchants}
            value={filters.merchantName ?? null}
            onChange={(_, value) => set('merchantName', value)}
            renderInput={(params) => <TextField {...params} label={t('filters.merchant.label')} />}
          />
          <TextField
            size='small'
            type='number'
            label={t('filters.amount.min')}
            value={filters.minAmount ?? ''}
            onChange={(event) => set('minAmount', event.target.value === '' ? undefined : Number(event.target.value))}
            inputProps={{ min: 0, step: '0.01', inputMode: 'decimal' }}
          />
          <TextField
            size='small'
            type='number'
            label={t('filters.amount.max')}
            value={filters.maxAmount ?? ''}
            onChange={(event) => set('maxAmount', event.target.value === '' ? undefined : Number(event.target.value))}
            inputProps={{ min: 0, step: '0.01', inputMode: 'decimal' }}
          />
        </Box>
      </Collapse>

      {(active.length > 0 || dirty) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
          {active.map((filter) => (
            <Chip key={filter.key} label={filter.label} onDelete={() => set(filter.key, undefined)} sx={{ bgcolor: tokens.accentSoft, color: tokens.accentDeep, fontWeight: 600 }} />
          ))}
          {dirty && (
            <Link component='button' type='button' onClick={clearAll} sx={{ fontSize: 13.5, fontWeight: 600, ml: active.length ? 0.5 : 0 }}>
              {t('filters.clear')}
            </Link>
          )}
        </Box>
      )}
    </Box>
  );
};
